import { cn } from "ui";

import { ProfileTablerIcon } from "src/components/ProfileTablerIcon";
import { type PublicProfilePageLink } from "src/pages/PublicProfilePage";
import { type ProfileTheme } from "src/profile/profileDesign";

function getLinkHref(link: PublicProfilePageLink): string {
  const resourceHref = link.resource.url ?? link.resource.fileUrl ?? null;

  if (!resourceHref) {
    return "#";
  }

  return resourceHref.startsWith("http")
    ? resourceHref
    : `https://${resourceHref}`;
}

function getLinkTitle(link: PublicProfilePageLink): string {
  if (link.titleOverride) {
    return link.titleOverride;
  }

  return (
    link.resource.title ?? link.resource.fileName ?? link.resource.url ?? ""
  );
}

interface PublicProfileLinkCardProps {
  link: PublicProfilePageLink;
  theme: ProfileTheme;
}

export function PublicProfileLinkCard(props: PublicProfileLinkCardProps) {
  const href = getLinkHref(props.link);
  const hasHref = href !== "#";
  const hasThumbnail = Boolean(
    props.link.thumbnailUrl || props.link.thumbnailIcon,
  );

  const className =
    "rounded-xl px-4 py-3 text-center ring-1 ring-black/[0.06] shadow-sm";
  const style = {
    backgroundColor: props.theme.buttonColor,
    color: props.theme.buttonTextColor,
  };

  const content = (
    <span
      className={cn(
        "items-center",
        hasThumbnail ? "grid grid-cols-[2rem_1fr_2rem] gap-2" : "block",
      )}
    >
      {hasThumbnail ? (
        <span className="flex size-8 items-center justify-center overflow-hidden rounded-md">
          {props.link.thumbnailUrl ? (
            <img
              src={props.link.thumbnailUrl}
              alt=""
              className="size-full object-cover"
            />
          ) : props.link.thumbnailIcon ? (
            <ProfileTablerIcon
              name={props.link.thumbnailIcon}
              className="size-5"
            />
          ) : null}
        </span>
      ) : null}
      <span className="text-sm font-medium">{getLinkTitle(props.link)}</span>
      {hasThumbnail ? <span className="size-8" aria-hidden /> : null}
    </span>
  );

  if (hasHref) {
    return (
      <a
        href={href}
        target="_blank"
        rel="ugc noopener noreferrer"
        className={className}
        style={style}
      >
        {content}
      </a>
    );
  }

  return (
    <div className={className} style={style}>
      {content}
    </div>
  );
}
